import { DrpApplication, DrpTierDataType } from '../pages/cyber-hub/types';
import { calculatePercentage } from './calculatePercentage';

/**
 * Processes applications data and generates DRP tier data for display in table format
 *
 * This function aggregates IT applications by their assigned tiers and calculates:
 * - Total number of applications per tier
 * - Number of applications with DRP (Disaster Recovery Plan) links per tier
 * - Tier colors for visual display
 *
 * @param applications - Array of IT applications with tier and DRP information
 * @returns Array of processed DRP tier data sorted by tier number (Tier 0, Tier 1, etc.)
 */
export const processDrpTierData = (
  applications: DrpApplication[],
): DrpTierDataType[] => {
  // Map to store aggregated data for each tier, avoiding duplicates
  const tierMap = new Map<
    string,
    { apps: number; drps: number; color: string; tierOrder: number }
  >();

  // Process each application and aggregate by tier
  applications.forEach((app) => {
    // Skip applications that don't have a tier assigned
    if (!app.tier?.name) {
      return;
    }

    const tierName = app.tier.name;
    // Check if application has a valid DRP link (non-empty and not just whitespace)
    const hasDrp = app.drpLink && app.drpLink.trim() !== '';

    // Initialize tier data if this is the first application for this tier
    if (!tierMap.has(tierName)) {
      // Extract numeric part from tier name for sorting (e.g., "Tier 1" -> 1, "Tier 0" -> 0)
      const tierNumber = parseInt(tierName.replace(/\D/g, '')) || 0;

      tierMap.set(tierName, {
        apps: 0,
        drps: 0,
        color: app.tier?.color || '#CDCDCD', // Use tier color or default gray
        tierOrder: tierNumber,
      });
    }

    // Get the tier data and increment counters
    const tierData = tierMap.get(tierName)!;
    tierData.apps += 1; // Always increment total applications
    if (hasDrp) {
      tierData.drps += 1; // Only increment if application has DRP
    }
  });

  // Convert Map to array and transform to required output format
  const result = Array.from(tierMap.entries())
    .map(([tier, data]) => ({
      tier,
      color: data.color,
      apps: data.apps,
      drps: data.drps,
    }))
    .sort((a, b) => {
      // Sort by tier number in ascending order (Tier 0, Tier 1, Tier 2, etc.)
      const aNumber = parseInt(a.tier.replace(/\D/g, '')) || 0;
      const bNumber = parseInt(b.tier.replace(/\D/g, '')) || 0;
      return aNumber - bNumber;
    });

  if (result.length === 0) {
    return [
      {
        tier: 'N/A',
        color: '#CDCDCD', // Gray color
        apps: 0,
        drps: 0,
      },
    ];
  }

  return result;
};

/**
 * Get comprehensive DRP (Disaster Recovery Plan) summary
 *
 * This function provides a complete analysis of applications' DRP status including:
 * - Tier-based breakdown (from processDrpTierData)
 * - Overall DRP statistics and progress metrics
 * - Calculated percentages for progress indicators
 *
 * Used primarily in dashboard components to display DRP compliance and status cards.
 *
 * @param applications - Array of IT applications with tier and DRP information
 * @returns Complete DRP analysis object containing tier data and summary statistics
 */
export const getDrpSummary = (applications: DrpApplication[]) => {
  // Get detailed tier breakdown using the existing tier processing function
  const tierData = processDrpTierData(applications);

  // Calculate total number of applications in the dataset
  const totalApplications = applications.length;

  // Count applications that have valid DRP links (non-empty and not just whitespace)
  const applicationsWithDrp = applications.filter(
    (app) => app.drpLink && app.drpLink.trim() !== '',
  ).length;

  // Calculate DRP coverage percentage, avoiding division by zero
  const drpProgressPercent = calculatePercentage(
    applicationsWithDrp,
    totalApplications,
  );

  return {
    tierData, // Array of tier-based DRP data for table display
    totalApplications, // Total count of applications
    applicationsWithDrp, // Count of applications with DRP links
    drpProgressPercent, // Percentage of applications with DRP (0-100)
  };
};
